import { useCallback, useEffect, useMemo } from 'react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  FieldExtensionComponentProps,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import { GithubAccessStatus } from '../GithubAccessStatus';
import { GITOPS_REPO_DEFAULT } from '../githubOrgAccess';
import {
  GITHUB_ACCESS_OK,
  GITHUB_ACCESS_PENDING,
} from '../githubAccessValidation';
import { useGithubAccess } from '../useGithubAccess';

interface UiOptions {
  secretsKey?: string;
  repoUrl?: string;
  scopes?: string[];
  /** Single target the token must be able to reach; prefer `probeRepos`. */
  probeRepo?: string;
  /**
   * Every repository (`owner/name`) or organization (`owner`) the run will
   * touch, in run order. Defaults to the gitops repo.
   */
  probeRepos?: string[];
  /**
   * Name of another form field holding a repoUrl (`github.com?owner=…&repo=…`)
   * whose repository the run will also touch — a RepoUrlPicker in the same
   * step, say. Probed alongside `probeRepos`.
   */
  probeFromField?: string;
}

/** `github.com?owner=x&repo=y` → `x/y`; anything else → undefined. */
function slugFromRepoUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.includes('?')) return undefined;
  const params = new URLSearchParams(value.slice(value.indexOf('?') + 1));
  const owner = params.get('owner');
  const repo = params.get('repo');
  return owner && repo ? `${owner}/${repo}` : undefined;
}

/**
 * Captures a GitHub token into a template secret, but only one that has been
 * proven against every repository and organization the run will touch. On
 * hulkag, which enforces SAML SSO, a token that was never authorized for the
 * org fails deep in the run with a bare 404 — this reports it on the form.
 */
export const GitHubOAuthField = (
  props: FieldExtensionComponentProps<string, UiOptions>,
) => {
  const { uiSchema, formContext, onChange } = props;
  const options = (uiSchema?.['ui:options'] as UiOptions | undefined) ?? {};
  const secretsKey = options.secretsKey ?? 'USER_OAUTH_TOKEN';
  const repoUrl = options.repoUrl ?? 'https://github.com/hulkag';
  const scopes = useMemo(() => options.scopes ?? ['repo'], [options.scopes]);

  const configApi = useApi(configApiRef);
  const { setSecrets } = useTemplateSecrets();

  const fromField = options.probeFromField
    ? slugFromRepoUrl(formContext?.formData?.[options.probeFromField])
    : undefined;

  // The field-derived repo comes first: a run that touches the user's own repo
  // does so before the gitops PR, and probing in run order means the first
  // problem reported is the first one the user has to fix.
  const targets = [
    ...(fromField ? [fromField] : []),
    ...(options.probeRepos ??
      (options.probeRepo ? [options.probeRepo] : undefined) ?? [
        configApi.getOptionalString('internalApps.gitopsRepo') ??
          GITOPS_REPO_DEFAULT,
      ]),
  ];

  const onToken = useCallback(
    (token: string) => setSecrets({ [secretsKey]: token }),
    [setSecrets, secretsKey],
  );

  const { state, access, error, problem, authorizeUrl, authorize } =
    useGithubAccess({
      targets,
      scopes,
      scmUrl: repoUrl,
      auto: true,
      onToken,
    });

  // Report the outcome as the field's own value, so the step cannot advance
  // until access is proven — see ./index.ts and ../githubAccessValidation.
  //
  // A sentinel, never the token: this value lands in the task's `values`,
  // which are persisted and logged. The token goes to `secrets` via onToken.
  //
  // And always a value, never undefined: the scaffolder only validates keys
  // present in formData, so clearing the field would skip the check instead
  // of failing it.
  useEffect(() => {
    if (state === 'ok') {
      onChange(GITHUB_ACCESS_OK);
      return;
    }
    // See RepoNameFromApp: "still checking" and "denied" are different
    // answers, and only one of them is the user's to fix.
    onChange(state === 'error' ? '' : GITHUB_ACCESS_PENDING);
  }, [state, onChange]);

  return (
    <GithubAccessStatus
      state={state}
      access={access}
      error={error}
      problem={problem}
      authorizeUrl={authorizeUrl}
      targets={targets}
      onRetry={o => authorize(o).catch(() => undefined)}
      okMessage="GitHub access authorized"
    />
  );
};
